import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import { useAllocateSlot, useGetSlots, useGetAds } from "../../queries/advertisement";
import { useGetCompanies, useGetNgos } from "../../queries/user";
import { useGetCampaigns } from "../../queries/campaign";
import { toast, ToastContainer } from "react-toastify";
import 'react-toastify/dist/ReactToastify.css';

const AllocateSlot = () => {
    const navigate = useNavigate();

    const {
        register,
        handleSubmit,
        formState: { errors, isSubmitting },
        watch,
        setValue,
    } = useForm({
        defaultValues: {
            slotId: '',
            adId: '',
            clientId: '',
            campaignId: '',
            campaignExpiryDate: '',
        }
    });

    // Fetch data for select options
    const { data: slotsList = [] } = useGetSlots();
    const { data: adsList = [] } = useGetAds();
    const { data: companiesList = [] } = useGetCompanies();
    const { data: ngosList = [] } = useGetNgos();
    const { data: campaignsList = [] } = useGetCampaigns();

    const { mutateAsync: allocateSlot, isPending: isAllocating } = useAllocateSlot();

    const watchedAdId = watch("adId");
    const watchedCampaignId = watch("campaignId");

    const onSubmit = async (data) => {
        try {
            await allocateSlot(data);
            toast.success('Slot allocated successfully');
            navigate('/advertisements/slot-allocations');
        } catch (error) {
            console.error('Error allocating slot:', error);
            toast.error(`Error allocating slot: ${error.message || 'Unknown error'}`);
        }
    };

    // Auto-populate client when ad is selected
    useEffect(() => {
        if (watchedAdId && adsList.length > 0) {
            const selectedAd = adsList.find(ad => ad._id === watchedAdId);
            if (selectedAd && selectedAd.clientId) {
                setValue('clientId', selectedAd.clientId._id || selectedAd.clientId);
            }
        }
    }, [watchedAdId, adsList, setValue]);

    // Auto-populate expiry date when campaign is selected
    useEffect(() => {
        if (watchedCampaignId && campaignsList.length > 0) {
            const selectedCampaign = campaignsList.find(campaign => campaign._id === watchedCampaignId);
            if (selectedCampaign && selectedCampaign.endDate) {
                setValue('campaignExpiryDate', selectedCampaign.endDate.split('T')[0]);
            }
        }
    }, [watchedCampaignId, campaignsList, setValue]);

    // Filter free slots only
    const freeSlots = slotsList.filter(slot => slot.status === 'Free' && slot.isActive);
    const clientOptions = [...companiesList, ...ngosList];

    return (
        <div>
            <section className="flex flex-col pt-5 w-full mt-5 bg-white rounded-md shadow-[0px_0px_25px_rgba(94,92,154,0.1)] max-md:max-w-full">
                <div className="flex flex-col pr-px pl-6 w-full max-md:pl-5 max-md:max-w-full">
                    <h1 className="self-start text-lg font-bold pb-4">
                        Allocate Slot
                    </h1>
                    
                    <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            {/* Slot Selection */}
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Slot *
                                </label>
                                <select
                                    {...register("slotId", { required: "Slot is required" })}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                >
                                    <option value="">Select Slot</option>
                                    {freeSlots?.map((slot) => (
                                        <option key={slot._id} value={slot._id}>
                                            {slot.slotName} - {slot.slotLocation}
                                        </option>
                                    ))}
                                </select>
                                {errors.slotId && <p className="text-red-500 text-sm mt-1">{errors.slotId.message}</p>}
                            </div>

                            {/* Advertisement Selection */}
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Advertisement *
                                </label>
                                <select
                                    {...register("adId", { required: "Advertisement is required" })}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                >
                                    <option value="">Select Advertisement</option>
                                    {adsList?.filter(ad => ad.isActive)?.map((ad) => (
                                        <option key={ad._id} value={ad._id}>
                                            {ad.name} ({ad.type})
                                        </option>
                                    ))}
                                </select>
                                {errors.adId && <p className="text-red-500 text-sm mt-1">{errors.adId.message}</p>}
                            </div>

                            {/* Client Selection */}
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Client *
                                </label>
                                <select
                                    {...register("clientId", { required: "Client is required" })}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                >
                                    <option value="">Select Client</option>
                                    {clientOptions?.map((client) => (
                                        <option key={client._id} value={client._id}>
                                            {client.companyName || client.ngoName || client.name}
                                        </option>
                                    ))}
                                </select>
                                {errors.clientId && <p className="text-red-500 text-sm mt-1">{errors.clientId.message}</p>}
                            </div>

                            {/* Campaign Selection */}
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Campaign
                                </label>
                                <select
                                    {...register("campaignId")}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                >
                                    <option value="">Select Campaign</option>
                                    {campaignsList?.filter(campaign => campaign.isActive)?.map((campaign) => (
                                        <option key={campaign._id} value={campaign._id}>
                                            {campaign.name}
                                        </option>
                                    ))}
                                </select>
                            </div>

                            {/* Campaign Expiry Date */}
                            <div className="md:col-span-2">
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Campaign Expiry Date
                                </label>
                                <input
                                    type="date"
                                    {...register("campaignExpiryDate")}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                />
                            </div>
                        </div>

                        <div className="flex justify-end gap-4 pt-6 border-t">
                            <button
                                type="button"
                                onClick={() => navigate('/advertisements/slot-allocations')}
                                className="px-4 py-2 text-gray-700 bg-gray-200 rounded-md hover:bg-gray-300 focus:outline-none focus:ring-2 focus:ring-gray-500"
                            >
                                Cancel
                            </button>
                            <button
                                type="submit"
                                disabled={isSubmitting || isAllocating}
                                className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50"
                            >
                                {isSubmitting || isAllocating ? 'Allocating...' : 'Allocate Slot'}
                            </button>
                        </div>
                    </form>
                </div>
            </section>
            <ToastContainer
                position="top-right"
                autoClose={3000}
            />
        </div>
    );
}

export default AllocateSlot;
